import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserRolesService } from '@bp-core/src/lib/services/portal/user-roles.service';
import { Navigation } from 'app/core/navigation/navigation.types';

import { CRM_WRITE_ROLES } from './crm-access.guard';

/**
 * "Mis tareas" (feature 054c · FR-015): la abre quien puede ser responsable de una tarea, que son
 * los roles con escritura en el CRM — jefatura, `advisor-writer` y `crm-operator` (Customer
 * Success)—. `crm-viewer` y `advisor-reader` no la ven: no pueden recibir tareas (FR-005).
 *
 * Solo decide PRESENTACIÓN (menú y ruta): el servidor valida `ViewOwnTasks`/`ViewTeamTasks` en
 * `ListTasks`.
 */
export const CRM_MY_TASKS_ROLES = CRM_WRITE_ROLES;

/** Id del ítem del menú lateral (`app/mock-api/common/navigation/data.ts`). */
export const CRM_MY_TASKS_NAV_ID = 'crm-my-tasks';

export function canSeeMyTasks(roles: readonly string[]): boolean {
  return roles.some(role => (CRM_MY_TASKS_ROLES as readonly string[]).includes(role));
}

/**
 * El ítem "Mis tareas" vive dentro del CRM: aparece solo con el CRM habilitado (interruptor y rol
 * de acceso, mismo `canAccessCrm` del ítem CRM) y con un rol que pueda recibir tareas. A diferencia
 * del resto de las secciones de asesores, NO se oculta a los usuarios solo-CS: su ruta está bajo
 * `/crm` y `crmOnlyRedirectGuard` la deja pasar (AS-18).
 *
 * Se aplica DESPUÉS de `applyCrmNavigationVisibility`, que excluye este id de su barrido.
 */
export function applyMyTasksNavigationVisibility(
  navigation: Navigation,
  canAccessCrm: () => boolean,
  canSee: () => boolean,
): void {
  const item = navigation.bp.find(candidate => candidate.id === CRM_MY_TASKS_NAV_ID);
  if (item) {
    item.hidden = () => !canAccessCrm() || !canSee();
  }
}

/** Ruta `/crm/mis-tareas`: sin rol de escritura vuelve al tablero del CRM. */
export const crmMyTasksGuard: CanActivateFn = () => {
  const router = inject(Router);
  const userRoles = inject(UserRolesService);
  userRoles.loadRolesFromToken();
  return canSeeMyTasks(userRoles.roles()) ? true : router.createUrlTree(['/crm/pipeline']);
};
